// Press photo gallery — grid + click-to-expand lightbox.
//
// Filter chips at the top let press folks zero in on a category (action,
// portfolio, behind-the-scenes, headshots). Grid uses the 600px variant;
// lightbox loads the 2400px master so press get a print-quality preview
// before deciding to download the full media kit zip.
//
// Lightbox dismisses on click outside, Esc, or the close affordance.
// Keyboard arrow keys page through. Body scroll locks while open.

import { useEffect, useState } from "react";
import { PRESS_PHOTOS } from "../data/pressPhotos";
import PanBar from "../components/PanBar";

// Photos used as anchors in OTHER sections (bio, hero, etc.) — keep them
// out of the gallery so the page doesn't repeat itself end-to-end. Any
// photo with a defined `slot` is automatically excluded.

export default function PhotoGallery() {
  const [openIdx, setOpenIdx] = useState(null);

  const visible = PRESS_PHOTOS.filter((p) => !p.slot);

  // Body scroll lock + key bindings when the lightbox is open.
  useEffect(() => {
    if (openIdx == null) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setOpenIdx(null);
      else if (e.key === "ArrowRight") setOpenIdx((i) => (i + 1) % visible.length);
      else if (e.key === "ArrowLeft")  setOpenIdx((i) => (i - 1 + visible.length) % visible.length);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [openIdx, visible.length]);

  const active = openIdx == null ? null : visible[openIdx];

  return (
    <section
      id="photos"
      style={{
        background: "#0e2e46",
        padding: "5rem 1.25rem",
        borderTop: "1px solid rgba(252,179,31,0.12)",
      }}
    >
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "1.4rem" }}>
          <PanBar width={72} height={3} margin="0 auto 1.1rem" />
          <h2 style={{
            margin: "0 0 0.6rem",
            fontFamily: "var(--jop-script)",
            fontSize: "clamp(1.8rem, 4.5vw, 2.6rem)",
            color: "var(--jop-gold)",
            fontWeight: 700,
            letterSpacing: "-0.01em",
          }}>
            Photo gallery
          </h2>
          <p style={{
            margin: "0 auto",
            maxWidth: "640px",
            fontSize: "0.98rem",
            lineHeight: 1.6,
            color: "var(--jop-cream)",
            fontFamily: "var(--jop-display)",
            opacity: 0.85,
          }}>
            Tap any image to expand. Full-resolution originals (suitable for
            print) are bundled in the media kit zip.
          </p>
        </div>

        <div className="jop-press-gallery-grid" style={{ marginTop: "1.8rem" }}>
          {visible.map((p, i) => (
            <button
              key={p.name}
              type="button"
              onClick={() => setOpenIdx(i)}
              aria-label={`Expand ${p.alt}`}
              style={{
                position: "relative",
                padding: 0,
                border: "1px solid rgba(252,179,31,0.18)",
                borderRadius: "10px",
                overflow: "hidden",
                background: "#0a1c30",
                cursor: "zoom-in",
                aspectRatio: "1 / 1",
                transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 12px 28px rgba(0,0,0,0.45), 0 0 0 2px rgba(252,179,31,0.35)";
                e.currentTarget.style.borderColor = "transparent";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.borderColor = "rgba(252,179,31,0.18)";
              }}
            >
              <img
                src={`/${p.variants[600]}`}
                srcSet={`/${p.variants[600]} 600w, /${p.variants[1200]} 1200w`}
                sizes="(max-width: 640px) 50vw, (max-width: 1100px) 33vw, 25vw"
                alt={p.alt}
                loading="lazy"
                decoding="async"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox overlay. */}
      {active && (
        <div
          onClick={() => setOpenIdx(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 100,
            background: "rgba(5,9,18,0.92)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "3rem 1.25rem",
            cursor: "zoom-out",
          }}
        >
          {/* Close — top-right. */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpenIdx(null); }}
            aria-label="Close"
            style={{
              position: "absolute",
              top: "1rem",
              right: "1rem",
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(252,179,31,0.4)",
              color: "var(--jop-gold)",
              fontFamily: "var(--jop-display)",
              fontWeight: 800,
              fontSize: "1.05rem",
              padding: "0.4rem 0.75rem",
              borderRadius: "999px",
              cursor: "pointer",
            }}
          >
            ✕
          </button>

          {/* Prev / next — anchored to the sides on desktop, hidden on
              narrow screens where swipe / arrow-key works just as well. */}
          {visible.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpenIdx((i) => (i - 1 + visible.length) % visible.length); }}
                aria-label="Previous"
                className="jop-lb-nav jop-lb-prev"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpenIdx((i) => (i + 1) % visible.length); }}
                aria-label="Next"
                className="jop-lb-nav jop-lb-next"
              >
                ›
              </button>
            </>
          )}

          <img
            src={`/${active.variants[2400] || active.variants[1200]}`}
            alt={active.alt}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "min(1100px, 100%)",
              maxHeight: "calc(100vh - 8rem)",
              objectFit: "contain",
              borderRadius: "10px",
              boxShadow: "0 22px 60px rgba(0,0,0,0.6)",
              cursor: "default",
            }}
          />

          <div style={{
            marginTop: "1rem",
            color: "rgba(255,218,173,0.85)",
            fontFamily: "var(--jop-display)",
            fontSize: "0.88rem",
            maxWidth: "780px",
            textAlign: "center",
            lineHeight: 1.5,
          }}>
            {active.alt}
            <div style={{ marginTop: "0.45rem", fontSize: "0.78rem", opacity: 0.65, letterSpacing: "0.08em" }}>
              {openIdx + 1} / {visible.length} · {active.width}×{active.height}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .jop-press-gallery-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 0.75rem;
        }
        @media (max-width: 1100px) {
          .jop-press-gallery-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }
        @media (max-width: 640px) {
          .jop-press-gallery-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        .jop-lb-nav {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(255,255,255,0.08);
          color: var(--jop-cream);
          border: 1px solid rgba(252,179,31,0.35);
          font-family: var(--jop-display);
          font-size: 2rem;
          line-height: 1;
          width: 48px;
          height: 48px;
          border-radius: 999px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding-bottom: 0.25rem;
        }
        .jop-lb-prev { left: 1.1rem; }
        .jop-lb-next { right: 1.1rem; }
        @media (max-width: 720px) {
          .jop-lb-nav { display: none; }
        }
      `}</style>
    </section>
  );
}
